import React, { useCallback, useEffect, useRef, useState, memo } from "react";
import { Portal } from "components/tooltip/Portal";
import { calculatePosition } from "./calculatePosition";
import CaretFillIcon from "./images/icons/caret-fill.svg?react";
import { usePosition } from "./useMobPosition";
import "./Tooltip.scss";

export type TPosition = "topLeft" | "topRight" | "topCenter" | "bottomLeft" |
"bottomRight" | "bottomCenter" | "leftStart" | "leftCenter" | "leftEnd" | "rightStart" | "rightCenter" | "rightEnd";

type TSize = "xs" | "xl";

interface ITooltip {
  isArrow?: boolean;
  children: React.ReactNode;
  anchorEl: HTMLElement | null;
  className?: string;
  active?: boolean;
  position?: TPosition;
  size?: TSize;
  onClose?: () => void;
}

interface CustomCSSProperties {
  "--left"?: string;
  "--top"?: string;
}

export const Tooltip: React.FC<ITooltip> = memo(({
  children,
  position = "topCenter",
  anchorEl,
  active,
  isArrow = true,
  className,
  size = "xs",
  onClose
}) => {
  const [coords, setCoords] = useState<CustomCSSProperties>({ "--left": "0px", "--top": "0px" });
  const [arrowCoords, setArrowCoords] = useState<React.CSSProperties>({ left: 0, top: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (tooltipRef.current && anchorEl) {
      const anchor = anchorEl.getBoundingClientRect();
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const { tooltipStyle: { top, left }, arrowStyle } = calculatePosition({ anchor, tooltip, position });

      setCoords({ "--top": `${top}px`, "--left": `${left}px` });
      setArrowCoords(arrowStyle);
    }
  }, [position, anchorEl, tooltipRef, active]);

  const mouseEnter = useCallback(() => {
    updatePosition();
    setShowTooltip(true);
  }, [updatePosition]);

  const mouseLeave = () => {
    setShowTooltip(false);
  };

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  usePosition(updatePosition);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        onClose
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  return (
    <Portal active={active}>
      <div className={className ?? ""}>
        <div
          onMouseLeave={mouseLeave}
          onMouseEnter={mouseEnter}
          ref={tooltipRef}
          style={{
            ...coords,
            opacity: showTooltip || active ? 1 : 0,
            visibility: showTooltip || active ? "visible" : "hidden"
          }}
          className={`Tooltip Tooltip_${size}`}
        >
          {isArrow && <span className="Tooltip-Arrow" style={arrowCoords}>
            <CaretFillIcon />
          </span>}
          {children}
        </div>
      </div>
    </Portal>
  );
});
